import React, { useEffect, useState } from "react";
import { useHistory } from "react-router-dom";
import {
  Table,
  Space,
  Tooltip,
  Button,
  PageHeader,
  Tag,
  Select,
  Input,
} from "antd";
import {
  EditOutlined,
  DeleteOutlined,
  PlusCircleOutlined,
  EyeOutlined,
  FileTextOutlined,
} from "@ant-design/icons";
import { handleApiSuccess, handleApiError, MyBreadcrumb, confirmDelete } from "utils";
import {
  billingInvoiceListService,
  billingInvoiceDeleteService,
} from "services/billing";
import { INVOICE_STATUSES, statusMeta, money } from "../constants";

const { Option } = Select;
const { Search } = Input;

export default function InvoiceIndex() {
  const history = useHistory();
  const [isLoading, setIsLoading] = useState(false);
  const [allData, setAllData] = useState([]);
  const [dataSource, setDataSource] = useState([]);
  const [statusFilter, setStatusFilter] = useState("");
  const [tagFilter, setTagFilter] = useState("");
  const [search, setSearch] = useState("");

  const allTags = Array.from(
    new Set(allData.flatMap((i) => i.tags || []))
  ).filter(Boolean);

  const columns = [
    {
      title: "Invoice #",
      dataIndex: "invoice_number",
      key: "invoice_number",
      render: (t, r) => <a onClick={() => history.push("/billing/invoices/view/" + r._id)}>{t}</a>,
    },
    {
      title: "Customer",
      key: "customer",
      render: (t, r) => (r.company && r.company.company_name) || r.bill_to_name || "-",
    },
    {
      title: "Service Period",
      dataIndex: "service_period_label",
      key: "service_period_label",
    },
    {
      title: "Date",
      dataIndex: "invoice_date",
      key: "invoice_date",
    },
    {
      title: "Due",
      dataIndex: "invoice_due_date",
      key: "invoice_due_date",
    },
    {
      title: "Total",
      dataIndex: "grand_total",
      key: "grand_total",
      align: "right",
      render: (t, r) => money(t, r.currency),
    },
    {
      title: "Paid",
      dataIndex: "amount_paid",
      key: "amount_paid",
      align: "right",
      render: (t, r) => money(t, r.currency),
    },
    {
      title: "Due Amt",
      dataIndex: "amount_due",
      key: "amount_due",
      align: "right",
      render: (t, r) => money(t, r.currency),
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      render: (t) => {
        const m = statusMeta(t);
        return <Tag color={m.color}>{m.label}</Tag>;
      },
    },
    {
      title: "Tags",
      dataIndex: "tags",
      key: "tags",
      render: (tags) => (tags || []).map((t) => <Tag key={t}>{t}</Tag>),
    },
    {
      title: "Action",
      key: "action",
      render: (text, record) => (
        <Space size="small">
          <Tooltip title="View">
            <Button
              onClick={() => history.push("/billing/invoices/view/" + record._id)}
              shape="circle"
              icon={<EyeOutlined />}
            />
          </Tooltip>
          <Tooltip title="Edit">
            <Button
              onClick={() => history.push("/billing/invoices/update/" + record._id)}
              type="primary"
              shape="circle"
              icon={<EditOutlined />}
            />
          </Tooltip>
          <Tooltip title="Delete">
            <Button
              onClick={() => deleteRecord(record._id)}
              shape="circle"
              icon={<DeleteOutlined />}
            />
          </Tooltip>
        </Space>
      ),
    },
  ];

  useEffect(() => {
    loadData();
  }, []);

  useEffect(() => {
    applyFilters();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusFilter, tagFilter, search, allData]);

  const loadData = () => {
    setIsLoading(true);
    billingInvoiceListService()
      .then((response) => {
        setIsLoading(false);
        setAllData(response.data.items || []);
      })
      .catch((error) => {
        setIsLoading(false);
        handleApiError(error);
      });
  };

  const applyFilters = () => {
    let rows = [...allData];
    if (statusFilter) rows = rows.filter((r) => r.status === statusFilter);
    if (tagFilter) rows = rows.filter((r) => (r.tags || []).includes(tagFilter));
    if (search) {
      const v = search.toLowerCase();
      rows = rows.filter(
        (r) =>
          (r.invoice_number || "").toLowerCase().includes(v) ||
          ((r.company && r.company.company_name) || "")
            .toLowerCase()
            .includes(v) ||
          (r.bill_to_name || "").toLowerCase().includes(v)
      );
    }
    setDataSource(rows);
  };

  const deleteRecord = (id) => {
    confirmDelete().then(() => {
      setIsLoading(true);
      billingInvoiceDeleteService(id)
        .then((response) => {
          handleApiSuccess(response);
          loadData();
        })
        .catch((error) => {
          setIsLoading(false);
          handleApiError(error);
        });
    });
  };

  return (
    <div>
      <MyBreadcrumb
        items={[
          { label: "Home", path: "/" },
          { label: "Billing", path: "" },
          { label: "Invoices", path: "" },
        ]}
      />
      <PageHeader
        ghost={false}
        avatar={{ icon: <FileTextOutlined /> }}
        title="Invoices"
        extra={[
          <Button
            key="create"
            shape="round"
            onClick={() => history.push("/billing/invoices/create")}
            type="primary"
            icon={<PlusCircleOutlined />}
          >
            New Invoice
          </Button>,
        ]}
      />
      <Space style={{ margin: "16px 0" }} wrap>
        <Search
          allowClear
          placeholder="Search invoice # or customer"
          onChange={(e) => setSearch(e.target.value)}
          style={{ width: 260 }}
        />
        <Select
          allowClear
          placeholder="Filter by status"
          style={{ width: 180 }}
          value={statusFilter || undefined}
          onChange={(v) => setStatusFilter(v || "")}
        >
          {INVOICE_STATUSES.map((s) => (
            <Option key={s.value} value={s.value}>
              {s.label}
            </Option>
          ))}
        </Select>
        <Select
          allowClear
          placeholder="Filter by tag"
          style={{ width: 180 }}
          value={tagFilter || undefined}
          onChange={(v) => setTagFilter(v || "")}
        >
          {allTags.map((t) => (
            <Option key={t} value={t}>
              {t}
            </Option>
          ))}
        </Select>
      </Space>
      <Table dataSource={dataSource} columns={columns} loading={isLoading} rowKey="_id" scroll={{ x: 1100 }} />
    </div>
  );
}
